/**
 * Cookie Preference Center Component
 *
 * Provides users with granular control over cookie categories and transparency
 * about what cookies are used and why. Complies with GDPR requirements for
 * user control and transparency.
 */

import { BarChart3, Building, Clock, Shield, Target, X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  CookieCategory,
  cookieManager,
  type CookieConsent,
  type CookieInfo,
} from "~/lib/cookie-manager";
import { cn } from "~/lib/utils";

interface CookiePreferenceCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (consent: CookieConsent) => void;
}

interface CategoryConfig {
  key: keyof Pick<CookieConsent, "essential" | "analytics" | "marketing">;
  category: CookieCategory;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  required: boolean;
}

const categoryConfigs: CategoryConfig[] = [
  {
    key: "essential",
    category: CookieCategory.ESSENTIAL,
    title: "Cookie Essenziali",
    description:
      "Necessari per il funzionamento base del sito web. Non possono essere disabilitati.",
    icon: Shield,
    required: true,
  },
  {
    key: "analytics",
    category: CookieCategory.ANALYTICS,
    title: "Cookie Analitici",
    description:
      "Ci aiutano a capire come i visitatori interagiscono con il sito per migliorare l'esperienza utente.",
    icon: BarChart3,
    required: false,
  },
  {
    key: "marketing",
    category: CookieCategory.MARKETING,
    title: "Cookie di Marketing",
    description:
      "Utilizzati per mostrare pubblicità personalizzata e misurare l'efficacia delle campagne.",
    icon: Target,
    required: false,
  },
];

export function CookiePreferenceCenter({
  isOpen,
  onClose,
  onSave,
}: CookiePreferenceCenterProps) {
  const [preferences, setPreferences] = useState<
    Pick<CookieConsent, "essential" | "analytics" | "marketing">
  >({
    essential: true,
    analytics: false,
    marketing: false,
  });

  const [activeTab, setActiveTab] = useState<"preferences" | "details">("preferences");
  const [cookieInventory, setCookieInventory] = useState<CookieInfo[]>([]);
  const [currentConsent, setCurrentConsent] = useState<CookieConsent | null>(null);

  // Load current preferences when component opens
  useEffect(() => {
    if (isOpen) {
      const consent = cookieManager.getConsent();
      setCurrentConsent(() => consent);

      if (consent) {
        setPreferences(() => ({
          essential: consent.essential,
          analytics: consent.analytics,
          marketing: consent.marketing,
        }));
      }

      setCookieInventory(() => cookieManager.getCookieInventory());
    }
  }, [isOpen]);

  const handlePreferenceChange = (
    category: keyof typeof preferences,
    enabled: boolean,
  ) => {
    if (category === "essential") return; // Essential cookies cannot be disabled

    setPreferences((prev) => ({
      ...prev,
      [category]: enabled,
    }));
  };

  const handleSavePreferences = () => {
    // Update consent with new preferences
    cookieManager.updateConsent({
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    });

    const updatedConsent = cookieManager.getConsent();
    if (updatedConsent && onSave) {
      onSave(updatedConsent);
    }

    onClose();
  };

  const handleAcceptAll = () => {
    const allEnabled = {
      analytics: true,
      marketing: true,
    };

    setPreferences((prev) => ({ ...prev, ...allEnabled }));
    cookieManager.updateConsent(allEnabled);

    const updatedConsent = cookieManager.getConsent();
    if (updatedConsent && onSave) {
      onSave(updatedConsent);
    }

    onClose();
  };

  const handleRejectAll = () => {
    const allDisabled = {
      analytics: false,
      marketing: false,
    };

    setPreferences((prev) => ({ ...prev, ...allDisabled }));
    cookieManager.updateConsent(allDisabled);

    const updatedConsent = cookieManager.getConsent();
    if (updatedConsent && onSave) {
      onSave(updatedConsent);
    }

    onClose();
  };

  const getCookiesForCategory = (category: CookieCategory): CookieInfo[] => {
    return cookieInventory.filter((cookie) => cookie.category === category);
  };

  const formatConsentDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/12 bg-black text-gray-200 shadow-2xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-start justify-between px-4 pt-4 pb-2">
          <div>
            <h2 className="tg-h4 font-bold">Centro preferenze cookie</h2>
            <p className="tg-small mt-1 text-gray-400">
              Gestisci le tue preferenze sui cookie e visualizza informazioni dettagliate
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-white transition-colors hover:bg-white/8"
            aria-label="Chiudi centro preferenze"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Current Consent Status */}
        {currentConsent && (
          <div className="bg-white/2 px-6 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Clock className="h-4 w-4" />
              <span>
                Ultimo consenso: {formatConsentDate(currentConsent.timestamp)}
                {Boolean(currentConsent.expiresAt) && (
                  <span className="ml-2">
                    • Scade: {formatConsentDate(currentConsent.expiresAt)}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-white/12">
          <nav className="flex px-4">
            <button
              type="button"
              onClick={() => setActiveTab("preferences")}
              className={cn(
                "tg-small border-b-2 px-4 py-3 font-bold transition-colors",
                activeTab === "preferences"
                  ? "border-tennis-400 text-gray-200"
                  : "border-transparent text-gray-400 hover:border-gray-400 hover:text-gray-200",
              )}
            >
              Preferenze
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={cn(
                "tg-small border-b-2 px-4 py-3 font-bold transition-colors",
                activeTab === "details"
                  ? "border-tennis-400 text-gray-200"
                  : "border-transparent text-gray-400 hover:border-gray-400 hover:text-gray-200",
              )}
            >
              Dettagli cookie
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="tg-scrollbar overflow-y-auto">
          {activeTab === "preferences" && (
            <div className="space-y-4 p-4">
              {categoryConfigs.map((config) => {
                const Icon = config.icon;
                const isEnabled = preferences[config.key];
                const cookiesInCategory = getCookiesForCategory(config.category);

                return (
                  <div key={config.key} className="rounded-lg bg-white/3 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex flex-1 items-start gap-4">
                        <div
                          className={cn(
                            "rounded-lg p-2",
                            config.required
                              ? "bg-tennis-800 text-white"
                              : isEnabled
                                ? "bg-green-300/40 text-white"
                                : "bg-white/24 text-white",
                          )}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="tg-primary">{config.title}</h3>
                            {config.required && (
                              <span className="bg-tennis-800 rounded-full px-2 py-1 text-xs font-medium text-white">
                                Richiesto
                              </span>
                            )}
                          </div>
                          <p className="tg-small mb-3 text-gray-400">
                            {config.description}
                          </p>
                          <div className="text-xs text-gray-400">
                            {cookiesInCategory.length} cookie
                            {cookiesInCategory.length !== 1 ? "s" : ""} in questa
                            categoria
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(e) =>
                              handlePreferenceChange(config.key, e.target.checked)
                            }
                            disabled={config.required}
                            className="sr-only"
                          />
                          <div
                            className={cn(
                              "h-6 w-11 rounded-full transition-colors duration-200 ease-in-out",
                              isEnabled ? "bg-tennis-900" : "bg-white/12",
                              config.required && "cursor-not-allowed",
                            )}
                          >
                            <div
                              className={cn(
                                "h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out",
                                isEnabled ? "translate-x-5.5" : "translate-x-0.5",
                                "mt-0.5",
                              )}
                            />
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "details" && (
            <div className="p-4">
              <div className="space-y-8">
                {categoryConfigs.map((config) => {
                  const cookiesInCategory = getCookiesForCategory(config.category);

                  if (cookiesInCategory.length === 0) return null;

                  return (
                    <div key={config.key}>
                      <h3 className="tg-primary mb-4 flex items-center gap-2">
                        <div className="bg-tennis-800 rounded-lg p-2 text-white">
                          <config.icon className="h-5 w-5 text-white" />
                        </div>
                        {config.title}
                      </h3>
                      <div className="space-y-4">
                        {cookiesInCategory.map((cookie) => (
                          <div key={cookie.name} className="rounded-lg bg-white/3 p-4">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <div>
                                <h4 className="mb-1 font-medium">{cookie.name}</h4>
                                <p className="mb-2 text-sm text-gray-400">
                                  {cookie.purpose}
                                </p>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  <span className="text-gray-400">
                                    Durata: {cookie.duration}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Building className="h-4 w-4 text-gray-400" />
                                  <span className="text-gray-400">
                                    Provider: {cookie.provider}
                                  </span>
                                </div>
                                {cookie.essential && (
                                  <div className="flex items-center gap-2">
                                    <Shield className="text-tennis-400 h-4 w-4" />
                                    <span className="text-tennis-400 font-medium">
                                      Essenziale
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 border-t border-white/12 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleRejectAll}
                className="tg-small rounded-lg border border-gray-600 bg-transparent px-4 py-2 font-bold text-gray-300 transition-colors hover:border-white hover:bg-white/12 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Rifiuta Tutti
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="bg-tennis-900 hover:bg-tennis-800 tg-small rounded-lg px-4 py-2 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                Accetta Tutti
              </button>
            </div>
            <button
              type="button"
              onClick={handleSavePreferences}
              className="bg-tennis-900 hover:bg-tennis-800 tg-small rounded-lg px-4 py-2 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              Salva Preferenze
            </button>
          </div>
          <p className="mt-3 text-center text-xs text-gray-400">
            Le tue preferenze verranno salvate per 1 anno. Puoi modificarle in qualsiasi
            momento.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CookiePreferenceCenter;
